import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from "../ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
    CardFooter,
} from "../ui/card";
import {
    Table,
    TableBody,
    TableCaption,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";

import { AlertCircle, Edit, List, X } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import { useApi } from "../../lib/ApiContext";
import { useAuth } from "../../lib/AuthContext";
import { validateCustomDomain } from "../../lib/validation";

const OrganizationEdit = () => {
    const { publicId } = useParams();
    const navigate = useNavigate();
    const api = useApi();
    const { user } = useAuth();

    const [organization, setOrganization] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [brandVoiceStore, setBrandVoiceStore] = useState(null);
    const [originSites, setOriginSites] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        custom_domain: '',
        default_destination: '',
    });
    const [isActive, setIsActive] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Brand voice loading state
    const [isBrandVoiceLoading, setIsBrandVoiceLoading] = useState(false);

    // Add Document dialog state
    const [showAddDocumentDialog, setShowAddDocumentDialog] = useState(false);
    const [documentFormData, setDocumentFormData] = useState({
        title: '',
        url: ''
    });
    const [isAddingDocument, setIsAddingDocument] = useState(false);

    // Delete Origin Site dialog state
    const [showDeleteOriginSiteDialog, setShowDeleteOriginSiteDialog] = useState(false);
    const [siteToDelete, setSiteToDelete] = useState(null);
    const [isDeletingOriginSite, setIsDeletingOriginSite] = useState(false);

    useEffect(() => {
        const fetchOrganization = async () => {
            try {
                const org = await api.organizations.getByPublicId(publicId);
                if (org) {
                    setOrganization(org);
                    // Fetch templates for this organization
                    try {
                        const templatesData = await api.templates.getAll(org.id);
                        setTemplates(templatesData || []);

                        // Fetch brand voice store and origin sites for this organization
                        try {
                            setIsBrandVoiceLoading(true);
                            const brandVoiceStoreData = await api.organizations.getBrandVoiceStore(org.id);
                            setBrandVoiceStore(brandVoiceStoreData);

                            // If we have a brand voice store, fetch origin sites
                            if (brandVoiceStoreData && brandVoiceStoreData.openai_vector_store_id) {
                                try {
                                    const originSitesData = await api.serviceBindings.listOriginSites(brandVoiceStoreData.openai_vector_store_id);
                                    if (originSitesData && originSitesData.success && originSitesData.origin_sites) {
                                        setOriginSites(originSitesData.origin_sites);
                                    } else {
                                        setOriginSites([]);
                                    }
                                } catch (err) {
                                    console.error("Failed to fetch origin sites:", err);
                                    setOriginSites([]);
                                }
                            } else {
                                setOriginSites([]);
                            }
                        } catch (err) {
                            console.error("Failed to fetch brand voice store:", err);
                            setBrandVoiceStore(null);
                            setOriginSites([]);
                        } finally {
                            setIsBrandVoiceLoading(false);
                        }
                    } catch (err) {
                        console.error("Failed to fetch templates:", err);
                        setTemplates([]);
                    }

                    setFormData({
                        name: org.name,
                        custom_domain: org.custom_domain || '',
                        default_destination: org.default_destination || '',
                    });

                    // Set active state from org data, default to true if not specified
                    setIsActive(org.active !== undefined ? org.active : true);
                } else {
                    // Organization not found
                    setError("Organization not found");
                }
            } catch (err) {
                console.error("Failed to fetch organization:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchOrganization();
    }, [publicId, api.organizations, api.templates, api.pages, api.serviceBindings]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const handleActiveToggle = async () => {
        // Toggle the active state locally first for responsive UI
        const newStatus = !isActive;
        setIsActive(newStatus);

        try {
            // Update the organization status via API
            const result = await api.organizations.updateStatus(organization.id, newStatus);
            // Update the organization with the new status
            setOrganization({
                ...organization,
                active: result.active
            });
        } catch (err) {
            // If there's an error, revert the UI state
            console.error("Failed to update organization status:", err);
            // Set error state
            setError(err.message);
            // Revert active state
            setIsActive(!newStatus);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate custom domain
        const domainValidation = validateCustomDomain(formData.custom_domain);
        if (!domainValidation.valid) {
            setError(domainValidation.message);
            return;
        }

        // Validate default destination URL
        if (!formData.default_destination) {
            setError("Default Destination URL is required.");
            return;
        }

        setIsSaving(true);

        try {
            // Update the organization via API
            const updatedOrg = await api.organizations.update(organization.id, formData);
            // Set updated data
            setOrganization(updatedOrg);
            navigate('/organizations');
        } catch (err) {
            console.error("Failed to update organization:", err);
            setError(err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleEditTemplate = (template) => {
        navigate(`/organizations/${organization.public_id}/templates/${template.public_id}/edit`);
    };

    const handleToggleTemplateStatus = async (template) => {
        try {
            const newStatus = !template.active;
            await api.templates.updateStatus(template.id, newStatus);

            // Update the templates list with the new status
            setTemplates(templates.map(t =>
                t.id === template.id ? { ...t, active: newStatus } : t
            ));
        } catch (err) {
            console.error("Failed to toggle template status:", err);
            setError(err.message);
        }
    };

    const handleToggleAutoCreate = async (template) => {
        try {
            const newAutoCreate = !Boolean(template.auto_create_pages);
            await api.templates.updateAutoCreatePages(template.id, newAutoCreate);

            // Update the templates list with the new auto create status
            setTemplates(templates.map(t =>
                t.id === template.id ? { ...t, auto_create_pages: newAutoCreate } : t
            ));
        } catch (err) {
            console.error("Failed to toggle auto create:", err);
            setError(err.message);
        }
    };

    const handleViewPages = (template) => {
        navigate(`/organizations/${organization.public_id}/templates/${template.public_id}/pages`);
    };

    const handleAddTemplate = () => {
        // Navigate to the template new page with the correct orgPublicId parameter
        navigate(`/organizations/${organization.public_id}/templates/new`);
    };

    const handleAddDocument = () => {
        // Reset form data and open dialog
        setDocumentFormData({
            title: '',
            url: ''
        });
        setShowAddDocumentDialog(true);
    };

    const handleDocumentFormChange = (e) => {
        const { name, value } = e.target;
        setDocumentFormData({
            ...documentFormData,
            [name]: value,
        });
    };

    const handleAddDocumentSubmit = async () => {
        // Validate form data
        if (!documentFormData.title.trim()) {
            setError("Title is required.");
            return;
        }

        if (!documentFormData.url.trim()) {
            setError("URL is required.");
            return;
        }

        // Basic URL validation
        try {
            new URL(documentFormData.url);
        } catch (e) {
            setError("Please enter a valid URL.");
            return;
        }

        setIsAddingDocument(true);
        setError(null);

        try {
            // Submit the document using the service binding
            await api.serviceBindings.submitBrandVoiceDocument({
                url: documentFormData.url,
                title: documentFormData.title,
                organizationId: parseInt(organization.id)
            });

            // Close dialog and reset form
            setShowAddDocumentDialog(false);
            setDocumentFormData({
                title: '',
                url: ''
            });

            // Refresh the origin sites data
            if (brandVoiceStore && brandVoiceStore.openai_vector_store_id) {
                try {
                    const originSitesData = await api.serviceBindings.listOriginSites(brandVoiceStore.openai_vector_store_id);
                    if (originSitesData && originSitesData.success && originSitesData.origin_sites) {
                        setOriginSites(originSitesData.origin_sites);
                    }
                } catch (err) {
                    console.error("Failed to refresh origin sites:", err);
                }
            }

        } catch (err) {
            console.error("Failed to add document:", err);
            setError(err.message);
        } finally {
            setIsAddingDocument(false);
        }
    };

    const handleDeleteOriginSite = (originSite) => {
        setSiteToDelete(originSite);
        setShowDeleteOriginSiteDialog(true);
    };

    const handleConfirmDeleteOriginSite = async () => {
        if (!siteToDelete || !brandVoiceStore?.openai_vector_store_id) {
            return;
        }

        setIsDeletingOriginSite(true);
        setError(null);

        try {
            const result = await api.serviceBindings.deleteOriginSite(
                brandVoiceStore.openai_vector_store_id,
                siteToDelete
            );

            if (result.success) {
                // Close dialog
                setShowDeleteOriginSiteDialog(false);
                setSiteToDelete(null);

                // Refresh the origin sites data
                try {
                    const originSitesData = await api.serviceBindings.listOriginSites(brandVoiceStore.openai_vector_store_id);
                    if (originSitesData && originSitesData.success && originSitesData.origin_sites) {
                        setOriginSites(originSitesData.origin_sites);
                    }
                } catch (err) {
                    console.error("Failed to refresh origin sites:", err);
                }
            } else {
                setError(result.error || 'Failed to delete origin site documents');
            }
        } catch (err) {
            console.error("Failed to delete origin site:", err);
            setError(err.message);
        } finally {
            setIsDeletingOriginSite(false);
        }
    };

    const handleDeleteOrganization = async () => {
        setIsDeleting(true);
        try {
            // Delete the organization via API
            await api.organizations.delete(organization.id);
            // Redirect to organizations list
            navigate('/organizations');
        } catch (err) {
            console.error("Failed to delete organization:", err);
            setError(err.message);
            setIsDeleting(false);
            setShowDeleteDialog(false);
        }
    };

    if (loading) {
        return (
            <div className="container mx-auto p-4">
                <div className="flex justify-center items-center h-64">
                    <div
                        className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"
                        data-testid="loading-spinner"
                    ></div>
                </div>
            </div>
        );
    }

    if (!organization) {
        return (
            <div className="container mx-auto p-4">
                <div className="text-center">
                    <h2 className="text-xl">Organization not found</h2>
                    <Button
                        variant="outline"
                        onClick={() => navigate('/organizations')}
                        className="mt-4"
                    >
                        Back to Organizations
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="container mx-auto p-4">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-bold">{organization.name}</h1>
                    <div className="space-x-2">
                        <Button variant="outline" onClick={() => navigate('/organizations')}>
                            Back to Organizations
                        </Button>
                        {user?.is_super_admin && (
                            <Button
                                variant="destructive"
                                onClick={() => setShowDeleteDialog(true)}
                            >
                                Delete Organization
                            </Button>
                        )}
                    </div>
                </div>

                {error && (
                    <Alert variant="destructive" className="mb-6">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Error</AlertTitle>
                        <AlertDescription>
                            {error}
                        </AlertDescription>
                    </Alert>
                )}

                <Card className="mb-8">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <div>
                            <CardTitle>Edit {organization.name}</CardTitle>
                            <CardDescription>
                                Update the organization's information.
                            </CardDescription>
                        </div>
                        <div className="flex items-center space-x-2">
                            <span className={`text-sm font-medium ${isActive ? 'text-green-600' : 'text-gray-500'}`}>
                                {isActive ? 'Active' : 'Inactive'}
                            </span>
                            <Switch
                                checked={isActive}
                                onCheckedChange={handleActiveToggle}
                                aria-label="Active status"
                            />
                        </div>
                    </CardHeader>
                    <form onSubmit={handleSubmit}>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="name" className="text-right">
                                    Name
                                </Label>
                                <Input
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="col-span-3"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="custom_domain" className="text-right">
                                    Custom Domain
                                </Label>
                                <Input
                                    id="custom_domain"
                                    name="custom_domain"
                                    value={formData.custom_domain}
                                    onChange={handleChange}
                                    type="url"
                                    placeholder="https://try.company.com"
                                    className="col-span-3"
                                    required
                                    disabled={!user?.is_super_admin}
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="default_destination" className="text-right">
                                    Default Destination
                                </Label>
                                <Input
                                    id="default_destination"
                                    name="default_destination"
                                    value={formData.default_destination}
                                    onChange={handleChange}
                                    type="url"
                                    placeholder="https://example.com/fallback"
                                    className="col-span-3"
                                    required
                                    disabled={!user?.is_super_admin}
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <div className="col-start-2 col-span-3">
                                    <p className="text-xs text-muted-foreground">
                                        When a request arrives that matches no existing routes, users will be redirected to this URL.
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                        <CardFooter className="flex justify-end space-x-2 pt-6">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate('/organizations')}
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isSaving}
                            >
                                {isSaving ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </CardFooter>
                    </form>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <div>
                            <CardTitle>Templates</CardTitle>
                            <CardDescription className="mt-1.5">
                                Manage templates for this organization.
                            </CardDescription>
                        </div>
                        {user?.is_super_admin && (
                            <Button onClick={handleAddTemplate}>
                                Add Template
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableCaption>List of all templates for this organization.</TableCaption>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Name</TableHead>
                                    {user?.is_super_admin && <TableHead>ID</TableHead>}
                                    {user?.is_super_admin && <TableHead>Model</TableHead>}
                                    <TableHead>Status</TableHead>
                                    <TableHead>Auto Create</TableHead>
                                    <TableHead className="w-[100px]">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {templates.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={user?.is_super_admin ? 6 : 4} className="text-center py-8 text-gray-500">
                                            No templates found. Click "Add Template" to create one.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    templates.map((template) => (
                                        <TableRow
                                            key={template.id}
                                            className="hover:bg-gray-50"
                                        >
                                            <TableCell className="font-medium">
                                                {template.name}
                                            </TableCell>
                                            {user?.is_super_admin && (
                                                <TableCell className="font-mono text-xs">
                                                    {template.unique_key || 'N/A'}
                                                </TableCell>
                                            )}
                                            {user?.is_super_admin && (
                                                <TableCell>
                                                    {template.llm_model || 'None'}
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                <button
                                                    onClick={() => handleToggleTemplateStatus(template)}
                                                    className={`px-2 py-1 rounded-full text-xs font-medium transition-colors hover:opacity-80 cursor-pointer ${template.active === true ? 'bg-green-100 text-green-600 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                                                >
                                                    {template.active === true ? 'Active' : 'Inactive'}
                                                </button>
                                            </TableCell>
                                            <TableCell>
                                                <button
                                                    onClick={() => handleToggleAutoCreate(template)}
                                                    className={`px-2 py-1 rounded-full text-xs font-medium transition-colors hover:opacity-80 cursor-pointer ${Boolean(template.auto_create_pages) ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                                                >
                                                    {Boolean(template.auto_create_pages) ? 'Enabled' : 'Disabled'}
                                                </button>
                                            </TableCell>
                                            <TableCell>
                                                <div className={`flex ${user?.is_super_admin ? 'space-x-2' : 'justify-center'}`}>
                                                    {user?.is_super_admin && (
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-8 px-2"
                                                            onClick={() => handleEditTemplate(template)}
                                                        >
                                                            <Edit className="h-4 w-4 mr-1" />
                                                            Edit
                                                        </Button>
                                                    )}
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-8 px-2"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleViewPages(template);
                                                        }}
                                                    >
                                                        <List className="h-4 w-4 mr-1" />
                                                        Pages
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                <Card className="mt-8">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <div>
                            <CardTitle>Brand Voice</CardTitle>
                            <CardDescription className="mt-1.5">
                                View brand voice origin sites and document counts for this organization.
                            </CardDescription>
                        </div>
                        <Button onClick={handleAddDocument}>
                            Add Document
                        </Button>
                    </CardHeader>
                    <CardContent>
                        {isBrandVoiceLoading ? (
                            <div className="flex justify-center items-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900"></div>
                                <span className="ml-2 text-gray-600">Loading brand voice data...</span>
                            </div>
                        ) : !brandVoiceStore ? (
                            <div className="text-center py-8 text-gray-500">
                                <p>No brand voice store found for this organization.</p>
                                <p className="text-sm mt-2">Brand voice documents must be configured through the main exactapage service.</p>
                            </div>
                        ) : (
                            <Table>
                                <TableCaption>Origin sites and document counts from OpenAI vector store.</TableCaption>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Origin Site</TableHead>
                                        <TableHead>Document Count</TableHead>
                                        <TableHead className="w-[100px]">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {originSites.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3} className="text-center py-8 text-gray-500">
                                                No origin sites found in the brand voice store.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        originSites.map((site, index) => (
                                            <TableRow
                                                key={`${site.origin_site}-${index}`}
                                                className="hover:bg-gray-50"
                                            >
                                                <TableCell className="font-medium">
                                                    {site.origin_site === 'unknown' ? (
                                                        <span className="text-gray-500 italic">Unknown/Direct Upload</span>
                                                    ) : (
                                                        site.origin_site
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {site.document_count}
                                                </TableCell>
                                                <TableCell>
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-8 px-2"
                                                        onClick={() => handleDeleteOriginSite(site.origin_site)}
                                                    >
                                                        <X className="h-4 w-4 mr-1" />
                                                        Delete
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Add Document Dialog */}
            <Dialog open={showAddDocumentDialog} onOpenChange={setShowAddDocumentDialog}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Add Brand Voice Document</DialogTitle>
                        <DialogDescription>
                            Add a new document to the brand voice for this organization.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="document-title" className="text-right">
                                Title
                            </Label>
                            <Input
                                id="document-title"
                                name="title"
                                value={documentFormData.title}
                                onChange={handleDocumentFormChange}
                                className="col-span-3"
                                placeholder="Document title"
                                required
                            />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="document-url" className="text-right">
                                URL
                            </Label>
                            <Input
                                id="document-url"
                                name="url"
                                type="url"
                                value={documentFormData.url}
                                onChange={handleDocumentFormChange}
                                className="col-span-3"
                                placeholder="https://example.com/page"
                                required
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowAddDocumentDialog(false)}
                            disabled={isAddingDocument}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={handleAddDocumentSubmit}
                            disabled={isAddingDocument}
                        >
                            {isAddingDocument ? 'Adding...' : 'Add Document'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Origin Site Confirmation Dialog */}
            <Dialog open={showDeleteOriginSiteDialog} onOpenChange={setShowDeleteOriginSiteDialog}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Delete Origin Site Documents</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete all documents from{' '}
                            <strong>
                                {siteToDelete === 'unknown' ? 'Unknown/Direct Upload' : siteToDelete}
                            </strong>?
                            This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                setShowDeleteOriginSiteDialog(false);
                                setSiteToDelete(null);
                            }}
                            disabled={isDeletingOriginSite}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={handleConfirmDeleteOriginSite}
                            disabled={isDeletingOriginSite}
                        >
                            {isDeletingOriginSite ? 'Deleting...' : 'Delete Documents'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Custom Delete Confirmation Modal */}
            {showDeleteDialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-background w-full max-w-md rounded-lg border p-6 shadow-lg">
                        <div className="flex flex-col space-y-2">
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-semibold">Delete Organization</h2>
                                <button
                                    className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none"
                                    onClick={() => setShowDeleteDialog(false)}
                                    disabled={isDeleting}
                                >
                                    <X className="h-4 w-4" />
                                    <span className="sr-only">Close</span>
                                </button>
                            </div>
                            <p className="text-sm text-muted-foreground">
                                Are you sure you want to delete <strong>{organization?.name}</strong>?
                                This will permanently remove the organization and all associated templates and pages.
                                This action cannot be undone.
                            </p>
                        </div>
                        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <Button
                                variant="outline"
                                onClick={() => setShowDeleteDialog(false)}
                                disabled={isDeleting}
                                data-testid="cancel-delete-button"
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={handleDeleteOrganization}
                                disabled={isDeleting}
                            >
                                {isDeleting ? 'Deleting...' : 'Delete Organization'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrganizationEdit; 